import { useEffect, useState } from "react";
import { AlertCircle, AlertTriangle, CheckCircle2, LoaderCircle, X } from "lucide-react";

import type { CompanyChecklist, CompanyChecklistItem } from "@/shared/api/api-client";
import { errorMessage } from "@/shared/api/http";
import { AppLink } from "@/shared/components/AppLink";
import {
  COMPANY_CHECKLIST_HELP,
  COMPANY_CHECKLIST_LABEL,
  type CompanyChecklistKey
} from "@/shared/vocabulary/company-checklist";

type CompanyChecklistCardProps = {
  checklist: CompanyChecklist | null;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  /** O link de Ajustes pra aba `tab` (a rota conhece a empresa). */
  settingsHref: (tab: string) => string;
  /** Em Ajustes a aba é estado local: o clique simples troca a aba na
   *  própria página (o link segue valendo pro clique do meio). */
  onOpenTab?: (tab: string) => void;
  /** A ação de um clique do item de canais; devolve a frase do resultado.
   *  Ausente (quem só lê) = o botão não aparece. */
  onStandardChannels?: () => Promise<string>;
  /** A empresa do card: com ela, o card ganha o X pra fechar (2026-11-03),
   *  lembrado NESTE navegador e só pra esta empresa. Ausente = sem X. */
  companyId?: string;
};

/** Onde o navegador guarda o card fechado: as pendências que existiam no
 *  momento de fechar, por empresa. */
export const dismissStorageKey = (companyId: string) => `company-checklist-dismissed:${companyId}`;

function readDismissed(companyId: string | undefined): string[] | null {
  if (!companyId) return null;
  try {
    const raw = window.localStorage.getItem(dismissStorageKey(companyId));
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === "string") : null;
  } catch {
    return null;
  }
}

function writeDismissed(companyId: string, pending: string[] | null) {
  try {
    if (pending === null) window.localStorage.removeItem(dismissStorageKey(companyId));
    else window.localStorage.setItem(dismissStorageKey(companyId), JSON.stringify(pending));
  } catch {
    // Navegador sem armazenamento (janela privada, bloqueio): o card só
    // não lembra que foi fechado.
  }
}

/** Fechado continua fechado enquanto não surgir pendência NOVA — uma que
 *  não estava na lista quando a pessoa fechou. Pendência resolvida não
 *  reabre; a nova reabre, porque pode ser o que faz a classificação parar. */
export function stillDismissed(dismissed: readonly string[] | null, pending: readonly string[]): boolean {
  return dismissed !== null && pending.every(key => dismissed.includes(key));
}

/** O texto do resumo do cabeçalho: "2 faltando · 1 atenção" ou "Tudo em ordem". */
export function checklistSummary(checklist: CompanyChecklist): string {
  const parts: string[] = [];
  if (checklist.missing > 0) parts.push(`${checklist.missing} faltando`);
  if (checklist.warning > 0) parts.push(`${checklist.warning} atenção`);
  return parts.length > 0 ? parts.join(" · ") : "Tudo em ordem";
}

function labelOf(key: string): string {
  return COMPANY_CHECKLIST_LABEL[key as CompanyChecklistKey] ?? key;
}

function helpOf(key: string): string | undefined {
  return COMPANY_CHECKLIST_HELP[key as CompanyChecklistKey];
}

function StatusIcon({ status }: { status: CompanyChecklistItem["status"] }) {
  // Forma além de cor: o ícone muda junto com o tom.
  if (status === "missing") return <AlertCircle size={18} aria-hidden="true" />;
  if (status === "warning") return <AlertTriangle size={18} aria-hidden="true" />;
  return <CheckCircle2 size={18} aria-hidden="true" />;
}

/**
 * O card "Configuração da empresa": o que falta cadastrar pra a esteira
 * classificar, com o link da aba que resolve. A lista e os textos do que
 * falta vêm calculados do servidor; o card só desenha. Itens em ordem ficam
 * recolhidos, e falha de carga fica DENTRO do card — nunca derruba a página.
 */
export function CompanyChecklistCard({
  checklist,
  loading = false,
  error = null,
  onRetry,
  settingsHref,
  onOpenTab,
  onStandardChannels,
  companyId
}: CompanyChecklistCardProps) {
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState<string[] | null>(() => readDismissed(companyId));
  // Outra empresa na mesma tela (troca de empresa sem desmontar): lê a dela.
  useEffect(() => setDismissed(readDismissed(companyId)), [companyId]);
  const [notice, setNotice] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const runStandardChannels = async () => {
    if (!onStandardChannels) return;
    setBusy(true);
    setNotice(null);
    setActionError(null);
    try {
      // A recarga do checklist acontece lá dentro, antes de a frase chegar.
      setNotice(await onStandardChannels());
    } catch (failure) {
      setActionError(errorMessage(failure, "Não foi possível criar os canais padrão."));
    } finally {
      setBusy(false);
    }
  };

  const renderItem = (item: CompanyChecklistItem) => {
    const help = helpOf(item.key);
    // O `tab` é a aba de Ajustes como a tela a conhece: quem decide é o
    // servidor, pra não haver um mapa de tradução aqui que caduque.
    const tab = item.tab ?? null;
    return (
      <li key={item.key} className={`checklist-item status-${item.status}`} data-status={item.status}>
        <span className="checklist-icon"><StatusIcon status={item.status} /></span>
        <div className="checklist-copy">
          <strong>{labelOf(item.key)}</strong>
          <small>{item.detail}</small>
          {help && item.status !== "ok" && <small className="checklist-help">{help}</small>}
        </div>
        {(item.action === "standard_channels" || tab) && (
          <div className="checklist-actions">
            {item.status !== "ok" && item.action === "standard_channels" && onStandardChannels && (
              <button
                type="button"
                className="secondary-button"
                onClick={() => void runStandardChannels()}
                disabled={busy}
              >
                {busy ? "Criando…" : "Criar canais padrão"}
              </button>
            )}
            {item.status !== "ok" && tab && (
              <AppLink
                className="text-button"
                to={settingsHref(tab)}
                onPlainClick={onOpenTab ? () => onOpenTab(tab) : undefined}
              >
                Resolver
              </AppLink>
            )}
          </div>
        )}
      </li>
    );
  };

  const pending = checklist?.items.filter(item => item.status !== "ok") ?? [];
  const inOrder = checklist?.items.filter(item => item.status === "ok") ?? [];
  const pendingKeys = pending.map(item => item.key);

  const dismiss = () => {
    if (!companyId) return;
    writeDismissed(companyId, pendingKeys);
    setDismissed(pendingKeys);
  };
  const reopen = () => {
    if (!companyId) return;
    writeDismissed(companyId, null);
    setDismissed(null);
  };

  // Fechado: uma linha discreta no lugar, com o resumo e o "Mostrar". Falha
  // de carga nunca fica escondida — o card volta pra mostrar o erro.
  if (companyId && !error && stillDismissed(dismissed, pendingKeys)) {
    return (
      <p className="company-checklist-collapsed" role="status">
        <span>Configuração da empresa{checklist ? `: ${checklistSummary(checklist)}` : ""}</span>
        <button type="button" className="text-button" onClick={reopen}>
          Mostrar
        </button>
      </p>
    );
  }

  return (
    <section className="card company-checklist" aria-label="Configuração da empresa" aria-busy={loading}>
      <div className="section-heading">
        <div>
          <span className="eyebrow">CONFIGURAÇÃO</span>
          <h2>Configuração da empresa</h2>
        </div>
        {checklist && (
          <span
            className={`checklist-summary ${checklist.missing > 0 ? "has-missing" : checklist.warning > 0 ? "has-warning" : "is-ok"}`}
            role="status"
          >
            {checklistSummary(checklist)}
          </span>
        )}
        {loading && !checklist && <LoaderCircle className="spin" size={16} aria-hidden="true" />}
        {companyId && (
          <button
            type="button"
            className="icon-btn checklist-dismiss"
            onClick={dismiss}
            aria-label="Fechar o aviso de configuração"
            title="Fechar. O aviso volta se aparecer uma pendência nova."
          >
            <X size={16} />
          </button>
        )}
      </div>

      {error && (
        <div className="checklist-error" role="alert">
          <span>Não foi possível conferir a configuração. {error}</span>
          {onRetry && <button type="button" onClick={onRetry}>Tentar de novo</button>}
        </div>
      )}

      {notice && <p className="checklist-notice" role="status">{notice}</p>}
      {actionError && <p className="checklist-error" role="alert">{actionError}</p>}

      {checklist && (
        <>
          {pending.length > 0 && <ul className="checklist-list">{pending.map(renderItem)}</ul>}
          {inOrder.length > 0 && (
            <details className="checklist-ok">
              <summary>Ver o que está em ordem ({inOrder.length})</summary>
              <ul className="checklist-list">{inOrder.map(renderItem)}</ul>
            </details>
          )}
        </>
      )}
    </section>
  );
}
